/**
 * Validates IFSC (Indian Financial System Code) format
 * IFSC format: 4 letters (bank code) + 0 + 6 alphanumeric characters = 11 characters total
 * @param ifsc - The IFSC code string to validate
 * @returns true if the IFSC code is valid, false otherwise
 */
export const isValidIFSC = (ifsc: string): boolean => {
  return /^[A-Z]{4}[0][A-Z0-9]{6}$/.test(ifsc);
};

/**
 * Validates SWIFT/BIC (Bank Identifier Code) format
 * SWIFT format: 4 letters (bank code) + 2 letters (country code) + 2 alphanumeric characters (location) + optional 3 characters (branch)
 * @param swiftCode - The SWIFT code string to validate
 * @returns true if the SWIFT code is valid, false otherwise
 */
export const isValidSwiftCode = (swiftCode: string): boolean => {
  return /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(swiftCode);
};

/**
 * Validates UPI (Unified Payments Interface) ID format
 * UPI format: username@bankname or phone@bankname
 * @param upi - The UPI ID string to validate
 * @returns true if the UPI ID is valid, false otherwise
 */
export const isValidUPI = (upi: string): boolean => {
  if (!upi || upi.length === 0) {
    return false;
  }
  return /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+$/.test(upi);
};

/**
 * Validates Bank Account Number
 * Bank account numbers typically range from 9 to 16 digits
 * @param accountNumber - The account number (string or number) to validate
 * @returns true if the account number is valid (9-16 digits), false otherwise
 */
export const isValidBankAccountNumber = (accountNumber: string | number): boolean => {
  // Convert to string and remove any whitespace
  const cleaned = String(accountNumber).replace(/\s/g, '');
  return /^[0-9]{9,16}$/.test(cleaned);
};

/**
 * Validates Bank Name
 * Allows letters, space, dot and comma. No leading space. No trailing space/dot/comma.
 */
export const isValidBankName = (name: string): boolean => {
  // Require at least one letter
  return /^(?=.*[A-Za-z])(?! )[A-Za-z. ,]+(?<![ .,])$/.test(name);
};

/**
 * Validates Bank Branch Name
 * Allows letters, digits, space, hyphen, slash, comma and dot. No leading space. No trailing space/hyphen/slash/comma/dot.
 */
export const isValidBankBranchName = (name: string): boolean => {
  // Require at least one letter (disallow purely numeric names)
  return /^(?=.*[A-Za-z])(?! )[A-Za-z0-9\-\/ ,.]+(?<![ \-\/,.])$/.test(name);
};

/**
 * Validates Bank Account Holder Name
 * Allows letters, numbers, spaces, and special characters (-, &, .)
 * Requires at least 3 characters with at least 1 alphabet
 * Empty strings are also acceptable
 * No leading or trailing spaces
 */
export const isValidAccountHolderName = (name: string): boolean => {
  // Allow empty string
  if (!name || name.length === 0) {
    return true;
  }

  // Require at least 3 characters, at least one letter, no leading/trailing spaces
  // Allowed characters: letters, numbers, spaces, -, &, ., '
  return /^(?=.*[A-Za-z])(?! )[A-Za-z0-9\-&.' ]{3,}(?<! )$/.test(name);
};
